import { Controller } from "@hotwired/stimulus"
import { put, destroy } from "@rails/request.js"
import flash from "../lib/flashes"
import tippy from 'tippy.js'
import Vue from "vue/dist/vue.esm"
import WidgetToggler from "../components/WidgetToggler.vue"

export default class extends Controller {
  static targets = ["container", "empty", "header"]
  static values = {
    id: String,
    type: String,
    can: Boolean,
    structureName: String,
    companyName: String,
    companyId: String
  }

  connect() {
    this.editIndex = -1
    this.newDescription = ""
    this.relations = []
    this.totalSize = 0
    
    this.initializeWidgetToggler()
    this.getRelations()
    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()

    if (this.vueInstance) {
      this.vueInstance.$destroy()
      this.vueInstance.$el.remove()
      this.vueInstance = null
    }
  }
  
  //TODO remove when we migrate widget-controller to stimulus in the future
  initializeWidgetToggler() {
    if (this.hasHeaderTarget) {
      this.headerTarget.innerHTML = '' // clear the header
      const wrapper = document.createElement('div')
      this.headerTarget.appendChild(wrapper)

      this.vueInstance = new Vue({
        el: wrapper,
        data() {
          return {
            count: 0
          }
        },
        render(h) {
          return h(WidgetToggler, {
            props: {
              name: 'Relater'
            }
          }, [`Related Items (${this.count})`])
        }
      })
    }
  }

  setupEventListeners() {
    this.relationsUpdatedListener = () => {
      this.getRelations()
    }
    window.addEventListener('relations:updated', this.relationsUpdatedListener)
  }

  removeEventListeners() {
    if (this.relationsUpdatedListener) {
      window.removeEventListener('relations:updated', this.relationsUpdatedListener)
    }
  }

  async getRelations() {
    try {
      const response = await fetch(`/relations/get_relations?id=${this.idValue}&type=${this.typeValue}`, {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const data = await response.json()
      this.relations = data.relations
      this.totalSize = data.total_size

      this.render()
    } catch (error) {
      console.error(error)
      flash("Something went wrong!", 'error')
    }
  }

  render() {
    this.updateCount()
    this.renderRelations()
    this.updateEmptyState()
  }

  updateCount() {
    if (this.vueInstance) {
      this.vueInstance.count = this.totalSize
    }
  }

  updateEmptyState() {
    if (this.hasEmptyTarget) {
      this.emptyTarget.style.display = this.totalSize === 0 ? 'block' : 'none'
    }
  }

  renderRelations() {
    if (!this.hasContainerTarget) return

    const relationsArray = Object.entries(this.relations)
    const relationsWithItems = relationsArray.filter(([key, r]) => {
      const hasItems = r.items && r.items.length > 0
      const hasInherited = r.inherited_items && r.inherited_items.length > 0
      return hasItems || hasInherited
    })

    if (relationsWithItems.length === 0) {
      this.containerTarget.innerHTML = ''
      return
    }

    const html = relationsWithItems.map(([key, r]) => this.renderRelationGroup(r)).join('')
    this.containerTarget.innerHTML = html
    this.initializeTooltips()
  }

  renderRelationGroup(r) {
    const inheritedItems = r.inherited_items || []
    const runItems = r.items || []

    let inheritedHtml = ''
    if (inheritedItems.length > 0) {
      const inheritedItemsHtml = inheritedItems.map((relation) => this.renderRelationItem(relation, r, true)).join('')
      inheritedHtml = `
        <div class='relation-inherited-label'>Inherited from process</div>
        ${inheritedItemsHtml}
      `
    }

    let runHtml = ''
    if (runItems.length > 0) {
      const divider = inheritedItems.length > 0 ? `<hr class='relation-section-divider'>` : ''
      const runItemsHtml = runItems.map((relation) => this.renderRelationItem(relation, r, false)).join('')
      runHtml = `${divider}${runItemsHtml}`
    }

    return `
      <header>
        <h1>
          ${r.linked ? `<i class='${r.icon}'></i>Tagged in ${r.name}` : `<i class='${r.icon}'></i>${r.name}`}
        </h1>
      </header>
      ${inheritedHtml}
      ${runHtml}
    `
  }

  renderRelationItem(relation, r, inherited = false) {
    const isEditing = !inherited && this.editIndex === relation.id
    const showButtons = !inherited && this.canValue && relation.id && !r.linked
    const isArchived = relation.discarded_at
    const isAsset = relation.object_type === 'Asset' || relation.toable_type === 'Asset'
    const hasAssetDescription = relation.asset_type && relation.company_name
    
    // Truncate company name for asset descriptions
    const truncatedCompanyName = hasAssetDescription ? this.truncateCompanyName(relation.company_name) : ''
    const showCompanyTooltip = hasAssetDescription && this.isCompanyTruncated(relation.company_name)
    
    return `
      <div data-relation-id="${relation.id}">
        <section class='indented'>
          <h3 class="relation-name">
            ${isArchived ? `<i class='fas fa-box-archive' data-tippy-content="Archived"></i>` : ''}
            <a href='${relation.url}' data-overflow-tooltip="${this.escapeHtml(relation.name)}">
              ${this.escapeHtml(relation.name)}
            </a>
            ${relation.description && !isEditing ? `<div class='asset-sidebar__box__description'>${this.escapeHtml(relation.description)}</div>` : ''}
          </h3>
          ${hasAssetDescription ? `<p class='asset-sidebar__box__description' ${showCompanyTooltip ? `data-tippy-content="${this.escapeHtml(relation.company_name)}"` : ''}>${this.escapeHtml(relation.asset_type)} / ${truncatedCompanyName}</p>` : ''}
          ${showButtons ? this.renderButtons(relation, isAsset) : ''}
          ${inherited ? this.renderInheritedButtons(relation) : ''}
        </section>
        ${isEditing ? this.renderEditForm(relation) : ''}
      </div>
    `
  }

  renderButtons(relation, isAsset) {
    const companyName = this.getCompanyName(relation)
    const truncatedCompanyName = this.truncateCompanyName(companyName)
    const showCompanyTooltip = this.isCompanyTruncated(companyName)
    const supportsPreview = this.supportsPreview(relation)
    
    return `
      <div class='button-group button-group--condensed'>
        <p class='asset-sidebar__box__description company-name-default' ${showCompanyTooltip ? `data-tippy-content="${this.escapeHtml(companyName)}"` : ''}>
          ${truncatedCompanyName}
        </p>
        
        <div class='button-group button-group--condensed relation-buttons'>
          ${supportsPreview ? `<a href="${relation.url}/preview" class='button button--icon button--unfilled button--icon--small' data-turbo-frame="sidepanel" data-tippy-content="Preview"><i class="fas fa-eye"></i></a>` : ''}
          <button type="button" data-action="click->relater#startEdit" data-relater-id-param="${relation.id}" data-relater-description-param="${this.escapeHtml(relation.description || '')}" class='button button--icon button--unfilled button--icon--small' data-tippy-content="Edit description"><i class="fas fa-edit"></i></button>
          <button type="button" data-action="click->relater#deleteRelation" data-relater-id-param="${relation.id}" class='button button--icon button--unfilled button--icon--small button--icon--danger' data-tippy-content="Remove relation"><i class='fas fa-trash-can'></i></button>
        </div>
      </div>
    `
  }

  renderInheritedButtons(relation) {
    const companyName = this.getCompanyName(relation)
    const truncatedCompanyName = this.truncateCompanyName(companyName)
    const showCompanyTooltip = this.isCompanyTruncated(companyName)
    const supportsPreview = this.supportsPreview(relation)

    return `
      <div class='button-group button-group--condensed'>
        <p class='asset-sidebar__box__description company-name-default' ${showCompanyTooltip ? `data-tippy-content="${this.escapeHtml(companyName)}"` : ''}>
          ${truncatedCompanyName}
        </p>
        
        <div class='button-group button-group--condensed relation-buttons'>
          ${supportsPreview ? `<a href="${relation.url}/preview" class='button button--icon button--unfilled button--icon--small' data-turbo-frame="sidepanel" data-tippy-content="Preview"><i class="fas fa-eye"></i></a>` : ''}
        </div>
      </div>
    `
  }

  supportsPreview(relation) {
    // Use toable_type to determine if preview is supported
    const supportedTypes = [
      'Asset',
      'Article',
      'AssetPassword',
      'Website',
      'Company'
    ]
    
    // PDF articles don't support preview
    if (relation.toable_type === 'Article' && relation.is_pdf) {
      return false
    }
    
    return supportedTypes.includes(relation.toable_type)
  }

  renderEditForm(relation) {
    return `
      <form data-action="submit->relater#saveEdit" class='asset-sidebar__form asset-sidebar__form--full indented inline-related-item-description-edit'>
        <input type='text' 
               data-relation-id="${relation.id}"
               placeholder="Describe relation..." 
               value="${this.escapeHtml(this.newDescription)}"
               data-action="keydown.esc->relater#cancelEdit keydown.enter->relater#saveEdit">
        <div class='button-group'>
          <button
            type="button"
            class="button button--icon button--icon--small button--secondary"
            data-action="click->relater#cancelEdit"
            data-tippy-content="Cancel"
          >
            <i class="fas fa-times"></i>
          </button>
          <button
            type="button"
            class="button button--icon button--icon--small button--secondary"
            data-action="click->relater#saveEdit"
            data-relater-id-param="${relation.id}"
            data-tippy-content="Save"
          >
            <i class="fas fa-check"></i>
          </button>
        </div>
      </form>
    `
  }

  getCompanyName(relation) {
    if (relation.company_name) {
      return relation.company_name
    } else if (relation.toable_type === 'Article') {
      return 'Central KB'
    }
    return '&nbsp;'
  }

  truncateCompanyName(companyName) {
    if (!companyName || companyName === '&nbsp;' || companyName.length <= 10) {
      return companyName
    }
    return companyName.substring(0, 10) + '...'
  }

  isCompanyTruncated(companyName) {
    if (!companyName || companyName === '&nbsp;') return false
    return companyName.length > 10
  }

  startEdit(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const id = event.params.id
    const description = event.params.description || ''
    
    this.editIndex = id
    this.newDescription = description
    
    this.render()
    
    // Focus the input after render
    requestAnimationFrame(() => {
      const input = this.element.querySelector(`input[data-relation-id="${id}"]`)
      if (input) {
        input.focus()
      }
    })
  }

  async saveEdit(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const id = this.editIndex
    if (id === -1) return
    
    // Get the current value from the input
    const input = this.element.querySelector(`input[data-relation-id="${id}"]`)
    if (input) {
      this.newDescription = input.value
    }
    
    try {
      const response = await put(`/relations/${id}`, {
        body: JSON.stringify({
          relation: { description: this.newDescription }
        }),
        contentType: 'application/json',
        responseKind: 'json'
      })
      
      if (response.ok) {
        this.resetEdit()
        await this.getRelations()
        flash("Relation Updated!", 'success')
      } else {
        flash("Something went wrong!", 'error')
      }
    } catch (error) {
      console.error(error)
      flash("Something went wrong!", 'error')
    }
  }

  cancelEdit(event) {
    event.preventDefault()
    event.stopPropagation()
    this.resetEdit()
    this.render()
  }

  async deleteRelation(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const id = event.params.id
    
    if (!confirm('Are you sure you want to delete this relation?')) {
      return
    }
    
    try {
      const response = await destroy(`/relations/${id}`, {
        responseKind: 'json'
      })
      
      if (response.ok) {
        this.resetEdit()
        await this.getRelations()
        flash("Relation removed!", 'success')
      } else {
        flash("Something went wrong!", 'error')
      }
    } catch (error) {
      console.error(error)
      flash("Something went wrong!", 'error')
    }
  }

  resetEdit() {
    this.editIndex = -1
    this.newDescription = ""
  }

  initializeTooltips() {
    requestAnimationFrame(() => {
      if (window.initializeTippy) {
        window.initializeTippy(this.containerTarget)
      } else {
        tippy(this.containerTarget.querySelectorAll('[data-tippy-content]'))
      }

      if (window.initializeOverflowTooltips) {
        window.initializeOverflowTooltips(this.containerTarget)
      }
    })
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
